// src/components/StockPage.js

//imports
import React, { useEffect, useState } from 'react'; //React is the core library for building UI's
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'; //A react hook for performing side effects in functional components
import styled from 'styled-components';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'; //A library for building charts in react
import { getStockData, getPredictions, saveLastPredictions } from '../services/api'; //Functions ipported from an API service module to fetch stock data and predictions
import ProgressBar from './ProgressBar';
import NavBar from './NavBar';
import Footer from './Footer';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StockInfo = styled.div``;

const StockSymbol = styled.h1`
  font-size: 32px;
  margin: 0;
  color: #FFFFFF;
`;

const StockPrice = styled.div`
  font-size: 24px;
  margin-top: 8px;
  color: ${props => props.isPositive ? '#34C759' : '#FF3B30'};
`;

const BackButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background-color: #f2f2f7;
  font-size: 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #e5e5ea;
  }
`;

const ChartContainer = styled.div`
  background: #0E0E10;
  border-radius: 12px;
  padding: 16px 16px 8px 16px;
  margin-bottom: 24px;
  border: 1px solid #1F1F20;
  height: 420px;
`;

const ModelInfo = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ModelTitle = styled.h2`
  margin: 0 0 16px 0;
  color: #1c1c1e;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const Metric = styled.div`
  padding: 16px;
  background: #f2f2f7;
  border-radius: 8px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #636366;
  margin-bottom: 4px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.isPositive ? '#34C759' : props.isNegative ? '#FF3B30' : '#1c1c1e'};
`;

const LoadingContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const LoadingTitle = styled.h2`
  margin: 0 0 16px 0;
  color: #1c1c1e;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const models = {
  1: {
    name: 'LSTM Neural Network',
    description: 'Deep learning model specialized in sequence prediction',
    metrics: ['Accuracy', 'RMSE', 'MAE']
  },
  2: {
    name: 'Random Forest',
    description: 'Ensemble learning method for classification and regression',
    metrics: ['Accuracy', 'R²', 'Feature Importance']
  },
  3: {
    name: 'Prophet Model',
    description: 'Forecasting model that handles seasonality and holidays',
    metrics: ['Accuracy', 'Trend', 'Seasonality']
  },
  4: {
    name: 'XGBoost',
    description: 'Gradient boosting model optimized for speed and performance',
    metrics: ['Accuracy', 'RMSE', 'Feature Impact']
  }
};

// Diamond-shaped dot for prediction points
const DiamondDot = ({ cx, cy, large }) => {
  const size = large ? 7 : 5;
  const half = size / 2;
  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  return (
    <svg x={cx - half} y={cy - half} width={size} height={size} viewBox="0 0 10 10">
      <polygon points="5,0 10,5 5,10 0,5" fill="#34C759" stroke="#34C759" strokeWidth="1" />
    </svg>
  );
};

const StockPage = () => { //Defines StockPage as a functional react component
  const { symbol } = useParams(); //Defines a variable called symbol that gets the value of the URL parameter
  const [searchParams] = useSearchParams();
  const modelId = searchParams.get('model') || '1';
  const navigate = useNavigate();
  
  const [stockData, setStockData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [predictionProgress, setPredictionProgress] = useState(0);
  const [dataLoadingProgress, setDataLoadingProgress] = useState(0);

  // Helpers
  const formatPct = (n) => {
    if (n == null || isNaN(n)) return '—';
    const v = Number(n);
    const sign = v >= 0 ? '+' : '';
    return `${sign}${Math.abs(v).toFixed(2)}%`;
  };

  const simulateProgress = () => {
    const dataLoadingTime = 2; // seconds
    const predictionTime = 5; // seconds
    
    let dataProgress = 0;
    const dataInterval = setInterval(() => {
      dataProgress += 5;
      setDataLoadingProgress(Math.min(dataProgress, 100));
      
      if (dataProgress >= 100) {
        clearInterval(dataInterval);
      }
    }, (dataLoadingTime * 1000) / 20); // 20 steps
    
    setTimeout(() => {
      let calcProgress = 0;
      const calcInterval = setInterval(() => {
        calcProgress += 3; 
        setPredictionProgress(Math.min(calcProgress, 100));
        
        if (calcProgress >= 100) {
          clearInterval(calcInterval);
        }
      }, (predictionTime * 1000) / 33); // 33 steps
    }, dataLoadingTime * 700); // Start when data is 70% loaded
    
    return () => {
      clearInterval(dataInterval);
    };
  };

  useEffect(() => {
    let clearFn = null;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const clearProgressSimulation = simulateProgress();
        clearFn = clearProgressSimulation;
        
        const [stockResponse, predictionResponse] = await Promise.all([
          getStockData(symbol),
          getPredictions(symbol)
        ]);
        
        setDataLoadingProgress(100);
        setPredictionProgress(100);
        
        setTimeout(() => {
          setStockData(stockResponse);
          setPredictions(predictionResponse);
          try { saveLastPredictions(symbol, { stockData: stockResponse, predictions: predictionResponse, at: Date.now() }); } catch (_) {}
          setLoading(false);
        }, 500);
        
      } catch (err) {
        setError('Failed to fetch data. Please try again later.');
        console.error('Error:', err);
        setLoading(false);
      }
    };

    fetchData();
    
    return () => {
      if (clearFn && typeof clearFn === 'function') {
        clearFn();
      }
    };
  }, [symbol]);

  if (loading) {
    return (
      <>
        <NavBar />
        <Container>
          <Header>
            <StockInfo>
              <StockSymbol>{symbol}</StockSymbol>
            </StockInfo>
          </Header>
          
          <LoadingContainer>
            <LoadingTitle>Loading Stock Data & Predictions</LoadingTitle>
            <ProgressContainer>
              <ProgressBar 
                progress={dataLoadingProgress}
                label="Fetching Market Data"
                timeRemaining={Math.ceil((100 - dataLoadingProgress) / 50)}
              />
              <ProgressBar 
                progress={predictionProgress}
                label="Calculating Predictions"
                timeRemaining={Math.ceil((100 - predictionProgress) / 20)}
              />
            </ProgressContainer>
          </LoadingContainer>
        </Container>
        <Footer />
      </>
    );
  }
  
  if (error) return (
    <>
      <NavBar />
      <Container>{error}</Container>
      <Footer />
    </>
  );
  if (!stockData || !predictions) return (
    <>
      <NavBar />
      <Container>No data available</Container>
      <Footer />
    </>
  );

  const currentModel = models[modelId];
  const priceChange = stockData.price - stockData.previousClose;
  const percentChange = (priceChange / stockData.previousClose) * 100;
  const isPositive = priceChange >= 0;

  return (
    <>
      <NavBar />
      <Container>
        <Header>
          <StockInfo>
            <StockSymbol>{symbol}</StockSymbol>
            <StockPrice isPositive={isPositive}>
              ${stockData.price.toFixed(2)} {' '}
              <span>
                {isPositive ? '+' : ''}{priceChange.toFixed(2)} ({percentChange.toFixed(2)}%)
              </span>
            </StockPrice>
          </StockInfo>
          <BackButton onClick={() => {
            navigate('/predict', { replace: false });
          }}>Return to Predict</BackButton>
        </Header>

      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={[...stockData.historicalData.map(p => ({ date: p.date, price: p.price })), { date: predictions.nextDate, prediction: predictions.models[modelId]?.prediction }]} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f1f20" />
            <XAxis dataKey="date" tick={{ fill: '#8e8e93', fontSize: 12 }} axisLine={false} tickLine={false} minTickGap={30} />
            <YAxis tick={{ fill: '#8e8e93', fontSize: 12 }} axisLine={false} tickLine={false} domain={["auto","auto"]} />
            <Tooltip contentStyle={{ background: '#111113', border: '1px solid #1F1F20', color: '#fff' }} labelStyle={{ color: '#C7C7CC' }} />
            <Legend wrapperStyle={{ color: '#C7C7CC' }} payload={[
              { value: 'Historical Price', type: 'line', id: 'legend-price', color: '#C7C7CC' },
              { value: 'Prediction', type: 'diamond', id: 'legend-prediction', color: '#34C759' },
            ]} />
            <Line type="monotone" dataKey="price" stroke="#C7C7CC" name="Historical Price" strokeWidth={2} dot={{ r: 1.8, stroke: '#C7C7CC' }} />
            <Line type="monotone" dataKey="prediction" stroke="#34C759" name="Prediction" strokeWidth={2} strokeDasharray="5 5" dot={<DiamondDot />} activeDot={<DiamondDot large />} />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>

      <ModelInfo>
        <ModelTitle>{currentModel?.name || 'Model'}</ModelTitle>
        <p>{currentModel?.description || ''}</p>
        <MetricsGrid>
          {(() => {
            const model = predictions.models[modelId] || {};
            const oneDay = model.predictions_1d || predictions?.multiTimeframe?.oneDay || null;
            const twoDay = model.predictions_2d || predictions?.multiTimeframe?.twoDay || null;
            const oneWeek = model.predictions_1w || predictions?.multiTimeframe?.oneWeek || null;
            const vals = [
              { label: 'Prediction (1D)', val: oneDay ? Number(oneDay.change_percent) : null },
              { label: 'Prediction (2D)', val: twoDay ? Number(twoDay.change_percent) : null },
              { label: 'Prediction (1W)', val: oneWeek ? Number(oneWeek.change_percent) : null },
            ];
            return vals.map(({ label, val }) => (
              <Metric key={label}>
                <MetricLabel>{label}</MetricLabel>
                <MetricValue isPositive={(val ?? 0) > 0} isNegative={(val ?? 0) < 0}>
                  {formatPct(val)}
                </MetricValue>
              </Metric>
            ));
          })()}
          <Metric>
            <MetricLabel>Model Accuracy</MetricLabel>
            <MetricValue>{Number(predictions.models[modelId]?.accuracy ?? 0).toFixed(2)}%</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>Confidence Score</MetricLabel>
            <MetricValue>{Number(predictions.models[modelId]?.confidence ?? 0).toFixed(2)}%</MetricValue>
          </Metric>
        </MetricsGrid>
      </ModelInfo>
      </Container>
      <Footer />
    </>
  );
};

export default StockPage;
